import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Notification marchand de NOUVELLE COMMANDE.
//
// En plus de la notification interne (cloche + push PWA, déjà émise par
// la fonction atomique `core_create_order_atomic`), on prévient le
// marchand par E-MAIL et par WHATSAPP via l'edge function
// `send-order-notification`. Tout est BEST-EFFORT : un échec d'envoi ne
// doit jamais faire échouer la commande, déjà créée en base.
// ─────────────────────────────────────────────────────────────

export interface MerchantOrderNotifyInput {
  /** id du marchand (= users.id = commerçant propriétaire). */
  commerceId: number | string | null | undefined;
  orderId?: number | string | null;
  customerName?: string | null;
  items?: Array<{ name?: string | null; quantity?: number | string | null }>;
  total?: number | string | null;
  currency?: string | null;
}

interface MerchantContact {
  email: string;
  name: string;
  whatsapp: string;
}

/** Récupère l'email / le téléphone (WhatsApp) / le nom du marchand. */
async function fetchMerchantContact(commerceId: number): Promise<MerchantContact | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('email, telephone, nomcommerce, name')
      .eq('id', commerceId)
      .maybeSingle();
    if (error || !data) return null;
    const row = data as { email?: string | null; telephone?: string | null; nomcommerce?: string | null; name?: string | null };
    const email = (row.email || '').trim();
    const whatsapp = (row.telephone || '').trim();
    const name = (row.nomcommerce || row.name || '').trim();
    if (!email && !whatsapp) return null;
    return { email, name, whatsapp };
  } catch {
    return null;
  }
}

/** Construit l'URL de la commande dans le tableau de bord marchand. */
function orderDashboardUrl(orderId?: number | string | null): string {
  const base = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
  const path = orderId ? `/dashboard/orders?order=${orderId}` : '/dashboard/orders';
  return `${base}${path}`;
}

/**
 * Prévient le marchand par e-mail / WhatsApp qu'une nouvelle commande est
 * arrivée. Silencieux en cas d'échec (best-effort).
 */
export async function notifyMerchantNewOrder(input: MerchantOrderNotifyInput): Promise<void> {
  const commerceId = Number(input.commerceId);
  if (!Number.isFinite(commerceId) || commerceId <= 0) return;

  const contact = await fetchMerchantContact(commerceId);
  if (!contact) return;

  const items = (input.items || [])
    .filter((i) => i && i.name)
    .map((i) => ({ name: String(i.name), quantity: i.quantity ?? 1 }));

  try {
    await supabase.functions.invoke('send-order-notification', {
      body: {
        merchantEmail: contact.email || undefined,
        merchantName: contact.name || undefined,
        merchantWhatsapp: contact.whatsapp || undefined,
        orderId: input.orderId ?? undefined,
        customerName: input.customerName || 'Client',
        items,
        total: input.total ?? undefined,
        currency: input.currency || undefined,
        orderPageUrl: orderDashboardUrl(input.orderId),
      },
    });
  } catch {
    /* best-effort : l'échec d'envoi ne remonte jamais */
  }
}